import { useMemo } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { paramsSelector, useStore } from '@liveHelper/models/store'
import {
  convertDateStringToNewTimezoneDate,
  getTimezone,
  getWeekdays,
} from '@liveHelper/utils/dateTime'
import type { TimeSlot } from '@oneToOne/shared/services/rest/types'

import { useContextTranslation } from '@core/i18n'

type OfficeHourDate = Array<TimeSlotDate>
interface TimeSlotDate {
  startsAt: Date
  endsAt: Date
}
/**
 * @returns An array of dates representing the office hours in the local timezone.
 */
const useLocalizedOfficeHourDates = (): OfficeHourDate[] => {
  const chatChannelConfig = useChatChannelConfig()

  return useMemo(() => {
    const officeHours = chatChannelConfig?.config?.office_hours
    if (!officeHours?.days || !officeHours.timezone) {
      return []
    }

    const { days, timezone } = officeHours
    const currentTimezone = getTimezone()

    const isDefinedTimeSlot = (
      timeSlot: TimeSlot,
    ): timeSlot is { starts_at: string; ends_at: string } => {
      return !!timeSlot.starts_at && !!timeSlot.ends_at
    }

    return days.map((day) =>
      Array.isArray(day)
        ? day.filter(isDefinedTimeSlot).map((timeSlot) => ({
            startsAt: convertDateStringToNewTimezoneDate(
              timeSlot.starts_at,
              timezone,
              currentTimezone,
            ),
            endsAt: convertDateStringToNewTimezoneDate(
              timeSlot.ends_at,
              timezone,
              currentTimezone,
            ),
          }))
        : [],
    )
  }, [chatChannelConfig])
}

/**
 * Returns the index of the next available office hour.
 * If the next available slot is on the same day, it will return the current day's index.
 *
 * @returns {number | undefined} - The index of the next available office hour, or `undefined` if no available slots are found.
 */
function getNextAvailableOfficeHourIndex(
  days: (OfficeHourDate | null)[],
): number | undefined {
  const now = new Date()
  const todayIndex = now.getDay()

  for (let offset = 0; offset < days.length; offset++) {
    const currentIndex = (todayIndex + offset) % days.length
    const day = days[currentIndex]

    if (!day || day.length === 0) {
      continue
    }

    // Check if it's today and if the current time is before the end of ANY office hour time slot
    if (currentIndex === todayIndex) {
      const isWithinOfficeHours = day.some((timeSlot) => now < timeSlot.endsAt)
      if (isWithinOfficeHours) {
        return currentIndex
      }
    } else {
      // For future days with office hours, return the index
      return currentIndex
    }
  }

  // If no future office hours are found, return today's index if it has office hours (even if outside office hours)
  if (days[todayIndex]) {
    return todayIndex
  }

  // No available office hours for the rest of the week
  return undefined
}

/**
 * Hook to format office hours.
 * eg. [
        { Sunday: 'Closed' },
        { Monday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' },
        { Tuesday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' },
        { Wednesday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' },
        { Thursday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' },
        { Friday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' },
        { Saturday: 'Closed' },
      ],
 */
export const useFormatOfficeHours = (): Record<string, string>[] => {
  const { t } = useContextTranslation()
  const { lang } = useStore(paramsSelector)
  const days = useLocalizedOfficeHourDates()

  return useMemo(() => {
    const weekdays = getWeekdays({ lang })
    const formatter = new Intl.DateTimeFormat(lang, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })

    return days.map((day, index) => {
      const dayName = index < weekdays.length ? weekdays[index] : ''

      if (day && day.length > 0) {
        const timeSlots = day
          .map((timeSlot) => {
            if (timeSlot?.startsAt && timeSlot?.endsAt) {
              const startTime = formatter.format(new Date(timeSlot.startsAt))
              const endTime = formatter.format(new Date(timeSlot.endsAt))
              return `${startTime} - ${endTime}`
            }
          })
          .join('\n')
        return { [dayName]: timeSlots }
      } else {
        return { [dayName]: t('oneToOne:Closed') }
      }
    })
  }, [t, days, lang])
}

/**
 * Hook to get the next available office hour.
 * eg. { Monday: '9:00 AM - 12:00 PM\n2:00 PM - 6:00 PM' }
 */
export const useNextAvailableOfficeHour = ():
  Record<string, string> | undefined => {
  const days = useLocalizedOfficeHourDates()
  const formatedOfficeHours = useFormatOfficeHours()

  const index = getNextAvailableOfficeHourIndex(days)
  if (index === undefined) {
    return undefined
  }

  return formatedOfficeHours[index]
}
